import type { PropertyDescriptor, SourceContext, SourceTypeDefinition } from '../core/definitions';
import type { SettingsBag } from '../core/model';
import { BaseSourceRuntime, bool, num, str } from './base';
import { COLOUR_PRESETS, colourProblem, rasterFit } from './textRender';

const MAX_DIMENSION = 8192;

/** Canvas pixels per layout pixel for a raster scale: whole quarter steps between 1 and 4. */
export function rasterRatio(scale: number): number {
  if (!Number.isFinite(scale) || scale <= 1) return 1;
  return Math.min(4, Math.ceil(scale * 4) / 4);
}

abstract class CanvasRuntime<S extends SettingsBag> extends BaseSourceRuntime<S> {
  protected readonly canvas = document.createElement('canvas');
  protected ratio = 1;

  /**
   * Draws a `width × height` (layout px) picture at the current raster ratio.
   * `draw` works in layout units; the context is pre-scaled.
   */
  protected paint(width: number, height: number, draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void, problem: string | null = null): void {
    const w = Math.round(num(width, 1, 1, MAX_DIMENSION));
    const h = Math.round(num(height, 1, 1, MAX_DIMENSION));
    if (problem) {
      // The previous picture must not stay on air under a setting the user believes is applied.
      this.clearVideo();
      this.fail('unknown', problem);
      return;
    }
    const fit = rasterFit(w, h, this.ratio);
    this.canvas.width = fit.canvasWidth;
    this.canvas.height = fit.canvasHeight;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      this.fail('unsupported', '2D canvas is unavailable');
      return;
    }
    ctx.setTransform(fit.canvasWidth / w, 0, 0, fit.canvasHeight / h, 0, 0);
    try {
      draw(ctx, w, h);
    } catch (err) {
      // An invalid gradient must not kill the source: show an error and drop the half-drawn
      // frame; the next valid settings update draws normally.
      this.clearVideo();
      this.fail('unknown', err instanceof Error ? err.message : String(err));
      return;
    }
    this.publishVideo(this.canvas, w, h, false, fit.ratio);
    this.setStatus('live');
  }

  protected release(): void {
    this.canvas.width = 0;
    this.canvas.height = 0;
  }
}

// ---------------------------------------------------------------------------
// color
// ---------------------------------------------------------------------------

/** `width` or `height` 0 follows the canvas (output) size, so a new Colour always fills the frame. */
export type ColorSettings = SettingsBag & { color: string; width: number; height: number };

class ColorRuntime extends CanvasRuntime<ColorSettings> {
  private settings: Partial<ColorSettings>;
  private drawnFor = '';

  constructor(ctx: SourceContext, settings: Partial<ColorSettings>) {
    super(ctx);
    this.settings = settings;
    this.draw();
  }

  update(settings: ColorSettings): void {
    if (this.destroyed) return;
    this.settings = settings;
    this.draw();
  }

  /** Follows output resolution changes, which reach sources through getVideoSettings only. */
  tick(): void {
    if (!this.destroyed && this.sizeKey() !== this.drawnFor) this.draw();
  }

  private size(): { w: number; h: number } {
    const video = this.ctx.getVideoSettings();
    const w = num(this.settings.width, 0, 0, MAX_DIMENSION);
    const h = num(this.settings.height, 0, 0, MAX_DIMENSION);
    return { w: w >= 1 ? w : video.width, h: h >= 1 ? h : video.height };
  }

  private sizeKey(): string {
    const { w, h } = this.size();
    return `${w}x${h}`;
  }

  private draw(): void {
    const color = str(this.settings.color, '#3b82f6');
    const { w, h } = this.size();
    this.drawnFor = `${w}x${h}`;
    // A solid fill is never soft when scaled, so it always rasterises 1:1.
    this.ratio = 1;
    this.paint(
      w,
      h,
      (ctx, cw, ch) => {
        ctx.fillStyle = color;
        ctx.fillRect(0, 0, cw, ch);
      },
      colourProblem({ Colour: color }),
    );
  }
}

export const colorDefinition: SourceTypeDefinition<ColorSettings> = {
  kind: 'color',
  displayName: 'Colour',
  caps: { video: true, audio: false },
  category: 'graphics',
  defaults: () => ({ color: '#3b82f6', width: 0, height: 0 }),
  properties: (): PropertyDescriptor[] => [
    { key: 'color', label: 'Colour', type: 'color', alpha: true, presets: COLOUR_PRESETS },
    { key: 'width', label: 'Width', type: 'number', min: 0, max: MAX_DIMENSION, step: 1, unit: 'px', description: '0 matches the canvas width.' },
    { key: 'height', label: 'Height', type: 'number', min: 0, max: MAX_DIMENSION, step: 1, unit: 'px', description: '0 matches the canvas height.' },
  ],
  create: (ctx, settings) => new ColorRuntime(ctx, settings),
};

// ---------------------------------------------------------------------------
// shape
// ---------------------------------------------------------------------------

export const SHAPES = ['rectangle', 'rounded-rectangle', 'circle', 'ellipse', 'line', 'arrow', 'polygon', 'star'] as const;
export type ShapeKind = (typeof SHAPES)[number];

export type ShapeSettings = SettingsBag & {
  shape: ShapeKind;
  width: number;
  height: number;
  cornerRadius: number;
  fill: 'solid' | 'linear-gradient' | 'radial-gradient';
  color: string;
  color2: string;
  gradientAngle: number;
  strokeColor: string;
  strokeWidth: number;
  /** Line and arrow shaft thickness. */
  lineWidth: number;
  lineCap: 'butt' | 'round';
  /** Arrow head length; the head is as wide as the box is high. */
  headLength: number;
  doubleHeaded: boolean;
  /** Polygon corners or star points. */
  sides: number;
  /** Star inner radius as a fraction of the outer one. */
  innerRatio: number;
};

/** Endpoints of a linear gradient that spans the whole box along `angleDeg` (0 = left to right, clockwise). */
export function gradientLine(width: number, height: number, angleDeg: number): { x0: number; y0: number; x1: number; y1: number } {
  const a = (angleDeg * Math.PI) / 180;
  const dx = Math.cos(a);
  const dy = Math.sin(a);
  const half = (Math.abs(width * dx) + Math.abs(height * dy)) / 2;
  const cx = width / 2;
  const cy = height / 2;
  return { x0: cx - dx * half, y0: cy - dy * half, x1: cx + dx * half, y1: cy + dy * half };
}

/**
 * Vertices of a regular polygon (`innerRatio` 1) or star inscribed in the
 * ellipse of radii rx, ry around (cx, cy), first point straight up.
 */
export function starPoints(cx: number, cy: number, rx: number, ry: number, points: number, innerRatio = 1): { x: number; y: number }[] {
  const n = Math.max(3, Math.round(points));
  const star = innerRatio < 1;
  const count = star ? n * 2 : n;
  const out: { x: number; y: number }[] = [];
  for (let i = 0; i < count; i++) {
    const r = star && i % 2 === 1 ? innerRatio : 1;
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / count;
    out.push({ x: cx + Math.cos(a) * rx * r, y: cy + Math.sin(a) * ry * r });
  }
  return out;
}

/** Outline of a right-pointing arrow filling a `w × h` box (optionally pointing both ways). */
export function arrowPoints(w: number, h: number, shaft: number, head: number, both: boolean): { x: number; y: number }[] {
  const cy = h / 2;
  const half = Math.min(shaft, h) / 2;
  const len = Math.max(0, Math.min(head, both ? w / 2 : w));
  const right = [
    { x: w - len, y: cy - half },
    { x: w - len, y: 0 },
    { x: w, y: cy },
    { x: w - len, y: h },
    { x: w - len, y: cy + half },
  ];
  const left = both
    ? [
        { x: len, y: cy + half },
        { x: len, y: h },
        { x: 0, y: cy },
        { x: len, y: 0 },
        { x: len, y: cy - half },
      ]
    : [
        { x: 0, y: cy + half },
        { x: 0, y: cy - half },
      ];
  return [...right, ...left];
}

function readShape(s: Partial<ShapeSettings>): ShapeKind {
  return (SHAPES as readonly string[]).includes(s.shape as string) ? (s.shape as ShapeKind) : 'rectangle';
}

class ShapeRuntime extends CanvasRuntime<ShapeSettings> {
  private settings: Partial<ShapeSettings>;

  constructor(ctx: SourceContext, settings: Partial<ShapeSettings>) {
    super(ctx);
    this.settings = settings;
    this.draw();
  }

  update(settings: ShapeSettings): void {
    if (this.destroyed) return;
    this.settings = settings;
    this.draw();
  }

  setRasterScale(scale: number): void {
    const ratio = rasterRatio(scale);
    if (this.destroyed || ratio === this.ratio) return;
    this.ratio = ratio;
    this.draw();
  }

  private draw(): void {
    const s = this.settings;
    const shape = readShape(s);
    const fill = s.fill === 'linear-gradient' || s.fill === 'radial-gradient' ? s.fill : 'solid';
    const color = str(s.color, '#ef4444');
    const color2 = str(s.color2, '#3b82f6');
    const stroke = str(s.strokeColor, '#ffffff');
    const strokeWidth = num(s.strokeWidth, 0, 0, 500);
    const radius = num(s.cornerRadius, 32, 0, 4096);
    const angle = num(s.gradientAngle, 0);
    const lineWidth = num(s.lineWidth, 8, 1, 4096);
    const lineCap = s.lineCap === 'round' ? 'round' : 'butt';
    const headLength = num(s.headLength, 60, 0, MAX_DIMENSION);
    const doubleHeaded = bool(s.doubleHeaded, false);
    const sides = Math.round(num(s.sides, 5, 3, 64));
    const innerRatio = num(s.innerRatio, 0.5, 0.05, 0.95);
    const problem = colourProblem({
      Colour: color,
      'Second colour': fill === 'solid' ? '' : color2,
      'Outline colour': strokeWidth > 0 ? stroke : '',
    });
    this.paint(
      num(s.width, 400),
      num(s.height, 300),
      (ctx, w, h) => {
        let style: string | CanvasGradient = color;
        if (fill === 'linear-gradient') {
          const g = gradientLine(w, h, angle);
          style = ctx.createLinearGradient(g.x0, g.y0, g.x1, g.y1);
          style.addColorStop(0, color);
          style.addColorStop(1, color2);
        } else if (fill === 'radial-gradient') {
          style = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) / 2);
          style.addColorStop(0, color);
          style.addColorStop(1, color2);
        }
        ctx.fillStyle = style;
        if (shape === 'line') {
          // A horizontal stroke through the middle of the box; length and angle come from the item transform.
          const cap = lineCap === 'round' ? Math.min(lineWidth, h) / 2 : 0;
          ctx.lineWidth = Math.min(lineWidth, h);
          ctx.lineCap = lineCap;
          ctx.strokeStyle = style;
          ctx.beginPath();
          ctx.moveTo(cap, h / 2);
          ctx.lineTo(w - cap, h / 2);
          ctx.stroke();
          return;
        }
        // Inset by half the stroke so the whole outline stays inside the canvas.
        const inset = strokeWidth / 2;
        const bw = Math.max(0, w - strokeWidth);
        const bh = Math.max(0, h - strokeWidth);
        ctx.beginPath();
        ctx.lineJoin = shape === 'rectangle' ? 'miter' : 'round';
        if (shape === 'ellipse') ctx.ellipse(w / 2, h / 2, bw / 2, bh / 2, 0, 0, Math.PI * 2);
        else if (shape === 'circle') ctx.arc(w / 2, h / 2, Math.min(bw, bh) / 2, 0, Math.PI * 2);
        else if (shape === 'rounded-rectangle') ctx.roundRect(inset, inset, bw, bh, Math.min(radius, bw / 2, bh / 2));
        else if (shape === 'rectangle') ctx.rect(inset, inset, bw, bh);
        else {
          const pts =
            shape === 'arrow'
              ? arrowPoints(bw, bh, lineWidth, headLength, doubleHeaded).map((p) => ({ x: p.x + inset, y: p.y + inset }))
              : starPoints(w / 2, h / 2, bw / 2, bh / 2, sides, shape === 'star' ? innerRatio : 1);
          pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
          ctx.closePath();
        }
        ctx.fill();
        if (strokeWidth > 0) {
          ctx.lineWidth = strokeWidth;
          ctx.strokeStyle = stroke;
          ctx.stroke();
        }
      },
      problem,
    );
  }
}

const isOneOf = (s: SettingsBag, ...kinds: ShapeKind[]) => kinds.includes(readShape(s as Partial<ShapeSettings>));

export const shapeDefinition: SourceTypeDefinition<ShapeSettings> = {
  kind: 'shape',
  displayName: 'Shape',
  caps: { video: true, audio: false },
  category: 'graphics',
  defaults: () => ({
    shape: 'rounded-rectangle',
    width: 400,
    height: 300,
    cornerRadius: 32,
    fill: 'solid',
    color: '#ef4444',
    color2: '#3b82f6',
    gradientAngle: 90,
    strokeColor: '#ffffff',
    strokeWidth: 0,
    lineWidth: 8,
    lineCap: 'butt',
    headLength: 60,
    doubleHeaded: false,
    sides: 5,
    innerRatio: 0.5,
  }),
  properties: (): PropertyDescriptor[] => [
    {
      key: 'shape',
      label: 'Shape',
      type: 'select',
      options: [
        { value: 'rectangle', label: 'Rectangle' },
        { value: 'rounded-rectangle', label: 'Rounded rectangle' },
        { value: 'circle', label: 'Circle' },
        { value: 'ellipse', label: 'Ellipse' },
        { value: 'line', label: 'Line' },
        { value: 'arrow', label: 'Arrow' },
        { value: 'polygon', label: 'Polygon' },
        { value: 'star', label: 'Star' },
      ],
    },
    { key: 'width', label: 'Width', type: 'number', min: 1, max: MAX_DIMENSION, step: 1, unit: 'px' },
    { key: 'height', label: 'Height', type: 'number', min: 1, max: MAX_DIMENSION, step: 1, unit: 'px' },
    { key: 'cornerRadius', label: 'Corner radius', type: 'number', min: 0, max: 4096, step: 1, unit: 'px', visibleWhen: (s) => isOneOf(s, 'rounded-rectangle') },
    { key: 'lineWidth', label: 'Thickness', type: 'number', min: 1, max: 4096, step: 1, unit: 'px', visibleWhen: (s) => isOneOf(s, 'line', 'arrow') },
    {
      key: 'lineCap',
      label: 'Line ends',
      type: 'select',
      options: [
        { value: 'butt', label: 'Square' },
        { value: 'round', label: 'Round' },
      ],
      visibleWhen: (s) => isOneOf(s, 'line'),
    },
    { key: 'headLength', label: 'Head length', type: 'number', min: 0, max: MAX_DIMENSION, step: 1, unit: 'px', visibleWhen: (s) => isOneOf(s, 'arrow') },
    { key: 'doubleHeaded', label: 'Head at both ends', type: 'boolean', visibleWhen: (s) => isOneOf(s, 'arrow') },
    { key: 'sides', label: 'Corners / points', type: 'number', min: 3, max: 64, step: 1, visibleWhen: (s) => isOneOf(s, 'polygon', 'star') },
    { key: 'innerRatio', label: 'Inner radius', type: 'number', min: 0.05, max: 0.95, step: 0.05, slider: true, visibleWhen: (s) => isOneOf(s, 'star') },
    {
      key: 'fill',
      label: 'Fill',
      type: 'select',
      options: [
        { value: 'solid', label: 'Solid' },
        { value: 'linear-gradient', label: 'Linear gradient' },
        { value: 'radial-gradient', label: 'Radial gradient' },
      ],
    },
    { key: 'color', label: 'Colour', type: 'color', alpha: true, presets: COLOUR_PRESETS },
    { key: 'color2', label: 'Second colour', type: 'color', alpha: true, presets: COLOUR_PRESETS, visibleWhen: (s) => s.fill === 'linear-gradient' || s.fill === 'radial-gradient' },
    { key: 'gradientAngle', label: 'Gradient angle', type: 'number', min: 0, max: 360, step: 1, unit: '°', visibleWhen: (s) => s.fill === 'linear-gradient' },
    { key: 'strokeWidth', label: 'Outline width', type: 'number', min: 0, max: 500, step: 1, unit: 'px', visibleWhen: (s) => !isOneOf(s, 'line') },
    { key: 'strokeColor', label: 'Outline colour', type: 'color', alpha: true, presets: COLOUR_PRESETS, visibleWhen: (s) => num(s.strokeWidth, 0) > 0 && !isOneOf(s, 'line') },
  ],
  create: (ctx, settings) => new ShapeRuntime(ctx, settings),
};
